import React, { useEffect, useState, useContext, useMemo } from "react";
import axiosInstance from "api/axiosInstance";
import { Card, Box } from "@mui/material";
import Tabs from "components/mas/Tabs";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import CoinRechargeTable from "./CoinRechargeTable";
import CoinUsageHistory from "./CoinUsageHistory";
import { LanguageContext } from "../../../../context/LanguageContext";
import { getUserProfile } from "api/userApi";
import { COLORS } from "theme/tokens";

// 2026-05-25 全新 MAS Coin 頁
// 上：MAS Coin title + 3 張用量狀態卡（白色 wrapper card）
// 中：tab「儲值方案 / 使用紀錄」
// 下：「選擇適合的儲值方案」+ 小額/大額 toggle + 卡片
export default function CoinUpgradeManager() {
  const [usageStatus, setUsageStatus] = useState(null);
  const [coinBalance, setCoinBalance] = useState(null);
  const [tab, setTab] = useState("recharge"); // recharge | history
  const [tier, setTier] = useState("small"); // small (<=50) | large (>50)

  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("CoinUpgradeManager", "plan/CoinUpgradeManager/CoinUpgradeManager_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.CoinUpgradeManager || { Main: {}, Error: {} },
    [componentLanguageData]
  );

  useEffect(() => {
    // 取 coin 用量
    axiosInstance
      .get("/strategy/usage-status")
      .then((res) => setUsageStatus(res.data))
      .catch((err) => console.error(LANGUAGE.Error.UsageStatusFailed, err));

    // 取 MAS Coin 餘額 (從 user profile)
    getUserProfile()
      .then((res) => {
        const u = res.data?.user || {};
        // 後端目前在 usageStatus.coinWalletBalance 裡有 coin，profile 不一定回
        // 如果 profile 有就用，否則 fallback 用 usageStatus
        if (u.coinBalance !== undefined) setCoinBalance(u.coinBalance);
      })
      .catch(() => {});
  }, [LANGUAGE.Error.UsageStatusFailed]);

  // 狀態卡資料來源（優先 coinBalance state，否則 usageStatus）
  const balance =
    coinBalance !== null ? coinBalance : usageStatus?.coinWalletBalance ?? 0;
  const strategyLeft = usageStatus
    ? Math.max(0, (usageStatus.coin_strategyLimit || 0) - (usageStatus.coin_strategyToday || 0))
    : 0;
  const strategyTotal = usageStatus?.coin_strategyLimit || 0;
  const backtestLeft = usageStatus
    ? Math.max(0, (usageStatus.coin_backtestLimit || 0) - (usageStatus.coin_backtestToday || 0))
    : 0;
  const backtestTotal = usageStatus?.coin_backtestLimit || 0;

  // 狀態卡（單一）
  const StatCard = ({ title, subtitle, value, total, unit, iconSrc }) => (
    <Card
      sx={{
        borderRadius: "12px",
        p: 2.5,
        height: "100%",
        boxShadow: "0 4px 12px 0 rgba(0,0,0,0.03)",
        border: "1px solid rgba(0,0,0,0.04)",
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box
          sx={{
            width: 60,
            height: 60,
            bgcolor: COLORS.grayLightest,
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={iconSrc}
            alt={title}
            onError={(e) => {
              e.target.style.display = "none";
            }}
            sx={{ width: 36, height: 36 }}
          />
        </Box>
        <MDTypography sx={{ fontSize: "12px", fontWeight: 500, color: COLORS.black }}>
          {LANGUAGE.Main.Remaining}
        </MDTypography>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end">
        <Box>
          <MDTypography sx={{ color: COLORS.black, fontSize: "16px", fontWeight: 500 }}>
            {title}
          </MDTypography>
          {subtitle && (
            <MDTypography sx={{ color: COLORS.grayDark, fontSize: "11px", fontWeight: 400, mt: 0.3 }}>
              {subtitle}
            </MDTypography>
          )}
        </Box>
        <Box textAlign="right">
          <MDTypography component="span" sx={{ color: COLORS.main, fontSize: "28px", fontWeight: 700 }}>
            {value}
          </MDTypography>
          {total !== undefined && (
            <MDTypography component="span" sx={{ color: COLORS.grayDark, fontSize: "14px", fontWeight: 500, ml: 0.5 }}>
              / {total}
            </MDTypography>
          )}
          {unit && (
            <MDTypography component="span" sx={{ color: COLORS.grayDark, fontSize: "14px", fontWeight: 500, ml: 0.5 }}>
              {unit}
            </MDTypography>
          )}
        </Box>
      </Box>
    </Card>
  );

  return (
    // 2026-05-25 背景色加深一階：#f8f9fa → #eef0f3，跟 sidebar/navbar 白色形成明確色塊區隔
    <MDBox p={3} sx={{ background: COLORS.pageBg, minHeight: "100vh" }}>
      {/* 上層白色容器：標題 + 3 張狀態卡 */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
        <MDTypography sx={{ fontSize: "20px", fontWeight: 700, color: COLORS.black, mb: 2 }}>
          {LANGUAGE.Main.PageTitle || "MAS Coin"}
        </MDTypography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 2,
          }}
        >
          <StatCard
            title={LANGUAGE.Main.StatCoinBalance || "MAS Coin"}
            value={balance}
            unit="Coin"
            iconSrc="/images/coin/coinstrategy_1.png"
          />
          <StatCard
            title={LANGUAGE.Main.StatCoinStrategy || "Coin 策略產生"}
            subtitle={LANGUAGE.Main.StatHelperFrom}
            value={strategyLeft}
            total={strategyTotal}
            iconSrc="/images/coin/coinstrategy_2.png"
          />
          <StatCard
            title={LANGUAGE.Main.StatCoinBacktest || "Coin 回測"}
            subtitle={LANGUAGE.Main.StatHelperFrom}
            value={backtestLeft}
            total={backtestTotal}
            iconSrc="/images/coin/coinstrategy_3.png"
          />
        </Box>
      </Card>

      {/* tab 區（白色容器） */}
      <Card sx={{ p: 3, borderRadius: 3, boxShadow: "0 4px 16px rgba(0,0,0,0.04)" }}>
        {/* tab pill group（外框放大版 — 2026-05-25 對齊截圖一 design） */}
        <Box sx={{ display: "flex", pb: 2.5, mb: 3, borderBottom: "1px solid #eef0f3" }}>
          <Box
            sx={{
              display: "inline-flex",
              width: "fit-content",
              background: COLORS.grayLightest,
              borderRadius: "12px",
              p: "6px",
              gap: "4px",
            }}
          >
            {[
              { key: "recharge", label: LANGUAGE.Main.TabRecharge || "儲值方案" },
              { key: "history", label: LANGUAGE.Main.TabHistory || "使用紀錄" },
            ].map((t) => {
              const active = tab === t.key;
              return (
                <Box
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  sx={{
                    minWidth: 120,
                    px: 3,
                    py: 1.2,
                    cursor: "pointer",
                    fontSize: "15px",
                    fontWeight: active ? 600 : 500,
                    color: active ? COLORS.black : COLORS.grayDark,
                    borderRadius: "8px",
                    background: active ? COLORS.white : "transparent",
                    boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                    transition: "all 0.2s",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    textAlign: "center",
                    "&:hover": { color: COLORS.black },
                  }}
                >
                  {t.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* tab content */}
        <Box>
          {tab === "recharge" && (
            <>
              {/* 區段標題 + tier toggle */}
              <Box textAlign="center" mb={3}>
                <MDTypography sx={{ fontSize: "22px", fontWeight: 700, color: COLORS.black, mb: 2 }}>
                  {LANGUAGE.Main.SectionTitle || "選擇適合的儲值方案"}
                </MDTypography>
                <Box display="flex" justifyContent="center">
                  <Tabs
                    variant="pill"
                    value={tier}
                    onChange={(v) => setTier(v)}
                    tabs={[
                      { value: "small", label: LANGUAGE.Main.TierSmall || "小額儲值" },
                      { value: "large", label: LANGUAGE.Main.TierLarge || "大額儲值" },
                    ]}
                  />
                </Box>
                <MDTypography sx={{ fontSize: "12px", color: COLORS.main, mt: 1 }}>
                  {LANGUAGE.Main.TierHint || "(大額儲值最高可省 50%)"}
                </MDTypography>
              </Box>

              <CoinRechargeTable tier={tier} />
            </>
          )}

          {tab === "history" && <CoinUsageHistory />}
        </Box>
      </Card>
    </MDBox>
  );
}
